import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Typography,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useLogoutUserMutation } from "redux/api/authApi";
import { toast } from "react-toastify";
import { UserRoles } from "Enums/UserRoles";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useAppSelector } from "redux/store";
import { IUserLoggedState } from "redux/features/loggedUserSlice";

interface UserCardProps {
  name: string;
  lastName: string;
  role: string;
  imageUrl?: string;
}

const UserCard: React.FC<UserCardProps> = ({
  name,
  lastName,
  role,
  imageUrl,
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { user:loggedUser } = useAppSelector(
        (state: { userLoggedState: IUserLoggedState }) => state.userLoggedState
      );

  const [logoutUser, { isLoading, isSuccess, error, isError }] =
    useLogoutUserMutation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getInitials = (name: string, lastName: string) => {
    return `${name.charAt(0)}${lastName.charAt(0)}`;
  };

  const onLogoutHandler = async () => {
    logoutUser();
  };

  const onChangePasswordHandler = async () => {
    navigate("/cambiar-contrasenia");
  };
  const handleToApiKey = () => {
    navigate("/api-key");
  };
  const handleToPeopleOfInterest = () => {
    navigate("/people-of-interest");
  };

  useEffect(() => {
    if (isSuccess) {
      window.location.href = "/login";
    }

    if (isError) {
      if (Array.isArray((error as any).data.error)) {
        (error as any).data.error.forEach((el: any) =>
          toast.error(el.message, {
            position: "top-right",
          })
        );
      } else {
        toast.error((error as any).data.message, {
          position: "top-right",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <Box display="flex" alignItems="center">
      <Avatar src={imageUrl} alt={`${name} ${lastName}`}>
        {!imageUrl && getInitials(name, lastName)}
      </Avatar>
      <Box ml={2}>
        <Typography variant="subtitle1" lineHeight={1} fontWeight={500}>
          {name} {lastName}
        </Typography>
        <Typography variant="body2" fontWeight={400} color="#718096">
          {formatMessage({ id: `userRoles.${role}` as keyof typeof UserRoles })}
        </Typography>
      </Box>
      <IconButton
        aria-label="more"
        id="action-button"
        aria-controls={open ? "user-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        sx={{ marginTop: "20px" }}
        id="user-menu"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: {
              maxHeight: 48 * 4.5,
              width: "20ch",
            },
          }
        }}
      >
        <MenuItem onClick={onChangePasswordHandler}>{formatMessage({ id: "changePassword" })}</MenuItem>
        <MenuItem onClick={handleClose}>{formatMessage({id:"userProfile"})}</MenuItem>
        {loggedUser?.role===UserRoles.ADMIN && (
          <MenuItem onClick={handleToApiKey}>{formatMessage({id:"company.key"})}</MenuItem>
        )}
        {loggedUser?.role===UserRoles.SUPERADMIN && (
          <MenuItem onClick={handleToPeopleOfInterest}>{formatMessage({id:"dowloadPeopleCSV.title"})}</MenuItem>
        )}
        <MenuItem onClick={onLogoutHandler}>{formatMessage({id:"logout_button"})}</MenuItem>
      </Menu>
    </Box>
  );
};

export default UserCard;
